/**
 * Partner artwork capability links (`/api/partner-artwork/<64 hex>/...`, and
 * the manual order ones under `/api/partner-artwork/manual/<64 hex>/...`) are
 * bearer links for server-side fetchers. The browser should never see one, but
 * if one ever does, it must not leave in a Sentry event, transaction,
 * breadcrumb or replay. Same rule as the backend `capability-path-redact.ts`:
 * slash, `%2F` or JSON-escaped `\/` separators, an optional `manual/`
 * segment, case-insensitive.
 */
const CAPABILITY_TOKEN_RE = /(partner-artwork(?:\/|%2F|\\\/)+(?:manual(?:\/|%2F|\\\/)+)?)[0-9a-f]{64}/giu;
const CAPABILITY_HINT = /partner-artwork/iu;
const MAX_DEPTH = 12;

/** CSS selector for Replay `block`: media whose source is a capability link. */
export const CAPABILITY_MEDIA_SELECTORS: readonly string[] = [
  'img[src*="partner-artwork" i]',
  'img[srcset*="partner-artwork" i]',
  'source[src*="partner-artwork" i]',
  'source[srcset*="partner-artwork" i]',
  'a[href*="partner-artwork" i]',
];

export function redactCapabilityUrls(value: string): string {
  return CAPABILITY_HINT.test(value) ? value.replace(CAPABILITY_TOKEN_RE, '$1:token') : value;
}

function isPlainObject(value: object): value is Record<string, unknown> {
  const proto = Object.getPrototypeOf(value) as unknown;
  return proto === Object.prototype || proto === null;
}

/** Assign only when changed; a frozen or read-only holder is left as it is. */
function trySet(holder: Record<string | number, unknown>, key: string | number, value: unknown): void {
  try {
    holder[key] = value;
  } catch {
    // Frozen / non-writable: never throw out of a Sentry hook.
  }
}

function scrubWalk(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') return redactCapabilityUrls(value);
  if (!value || typeof value !== 'object' || depth > MAX_DEPTH) return value;
  // Plain data only: never descend into DOM nodes, class instances, Maps, etc.
  if (!Array.isArray(value) && !isPlainObject(value)) return value;
  if (seen.has(value)) return value;
  seen.add(value);
  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      const current: unknown = value[index];
      const scrubbed = scrubWalk(current, depth + 1, seen);
      if (scrubbed !== current) trySet(value as unknown[] as Record<number, unknown>, index, scrubbed);
    }
    return value;
  }
  const record = value;
  for (const key of Object.keys(record)) {
    const current = record[key];
    const scrubbed = scrubWalk(current, depth + 1, seen);
    const safeKey = redactCapabilityUrls(key);
    if (safeKey !== key) {
      try {
        delete record[key];
      } catch {
        // Frozen: leave it.
      }
      trySet(record, safeKey, scrubbed);
    } else if (scrubbed !== current) {
      trySet(record, key, scrubbed);
    }
  }
  return value;
}

/**
 * Redact every string (and object key) in place, depth-bounded, cycle-safe,
 * plain objects and arrays only. Returns the same object so it can be used
 * directly as a Sentry `beforeSend*` hook body.
 */
export function scrubCapabilityUrls<T>(value: T): T {
  return scrubWalk(value, 0, new WeakSet()) as T;
}

const BREADCRUMB_DATA_URL_FIELDS = ['url', 'from', 'to'] as const;

interface BreadcrumbLike {
  message?: unknown;
  data?: unknown;
}

/**
 * Breadcrumbs can carry live objects (console arguments may be DOM nodes or
 * app state), so only the known string fields are touched: `message`,
 * `data.url`, `data.from`, `data.to`, and string items of `data.arguments`.
 */
export function scrubCapabilityBreadcrumb<T extends BreadcrumbLike>(breadcrumb: T): T {
  if (typeof breadcrumb.message === 'string') {
    const message = redactCapabilityUrls(breadcrumb.message);
    if (message !== breadcrumb.message) trySet(breadcrumb as Record<string, unknown>, 'message', message);
  }
  const data = breadcrumb.data;
  if (!data || typeof data !== 'object') return breadcrumb;
  const record = data as Record<string, unknown>;
  for (const field of BREADCRUMB_DATA_URL_FIELDS) {
    const current = record[field];
    if (typeof current !== 'string') continue;
    const scrubbed = redactCapabilityUrls(current);
    if (scrubbed !== current) trySet(record, field, scrubbed);
  }
  const args = record.arguments;
  if (Array.isArray(args)) {
    for (let index = 0; index < args.length; index += 1) {
      const current: unknown = args[index];
      if (typeof current !== 'string') continue;
      const scrubbed = redactCapabilityUrls(current);
      if (scrubbed !== current) trySet(args as unknown[] as Record<number, unknown>, index, scrubbed);
    }
  }
  return breadcrumb;
}
